import { useEffect, useMemo, useState, useCallback } from "react";
import * as R from "ramda";
import { isNilOrEmpty, useSessionCtx } from "utils";

const defaultPollingOptions = {
  pollingInterval: 1000,
  pollingTimeoutMS: -1, // no timeout, poll forever
  onTimeout: () => {},
};

const poll = (debug, pollingFn, options) => {
  const { pollingInterval, pollingTimeoutMS } = options;
  const now = Date.now();

  // add 100 millis to account for time spent in setup
  const timeoutMS = now + pollingTimeoutMS + 100;
  debug("usePolling__starting interval: ", pollingInterval, pollingTimeoutMS, timeoutMS);

  let pollAttempts = 0;
  const intervalId = setInterval(async () => {
    const stopPolling = () => clearInterval(intervalId);
    pollAttempts++;

    debug("usePolling__attempt:", pollAttempts, timeoutMS - Date.now());
    if (options.pollingTimeoutMS > 0 && timeoutMS < Date.now()) {
      options.onTimeout();
      stopPolling();
    }
    const pollingInfo = {
      pollAttempts,
      stopPolling,
    };
    const result = await pollingFn(pollingInfo);
    debug("usePolling__insideinterval__pollingFn__result: ", result);
  }, pollingInterval);
  return intervalId;
};

export const usePolling = (pollingFn, pollingOptions = {}) => {
  if (isNilOrEmpty(pollingFn)) {
    throw new Error("Cannot start polling without a polling function.");
  }
  const options = useMemo(() => R.mergeDeepRight(defaultPollingOptions, pollingOptions), [
    pollingOptions,
  ]);
  const { debug } = useSessionCtx();

  const [started, setStarted] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    // only start if not already started
    if (started && isNilOrEmpty(intervalId)) {
      setIntervalId(poll(debug, pollingFn, options));
    }
  }, [debug, started, pollingFn, options, intervalId]);

  const startPolling = useCallback(() => {
    if (!started) {
      setStarted(true);
    }
  }, [started]);

  const resetPolling = useCallback(() => {
    debug("resetPolling");
    setStarted(false);
    setIntervalId(null);
  }, [debug]);

  return [startPolling, resetPolling];
};
